

export type EventType = (callback: (value?:any) => any, context?: any) => any;
export type EventListenerType = (callback: (value?:any) => any, context?: any, once?: boolean) => any;

/**
 * Custom event handler with typed arguments support and context binding
 * @param {Function} callback
 * @param {Object} context
 * @param {Boolean} once
 * @return {Object}
 * 
 * @example //Simple event
 * const event = new Event();
 * event.on((value) => {
 *  console.log(value);
 * });
 * event.emit('hello world');
 * 
 * @example //Event with typed arguments
 * const event = new Event<{name: string, age: number}>();
 * event.on((value) => {
 *  console.log(value.name);
 *  console.log(value.age);
 * });
 */

export default class Event<T = any | undefined> {
  handlers: any;
  typeCast: T | undefined;
  on = (callback: (args:T) => void, context?: any, once = false) => {
    if (this.handlers === undefined) {
      this.handlers = [];
    }
    this.handlers.push({
      callback: callback,
      context: context,
      once: once,
    });
  }

  uniq = (callback: (args:T) => void, context?: any, once = false) => {
    if (this.exists(callback, context)) return;
    this.on(callback, context, once);
  }

  exists = (callback: (args:T) => void, context?: any) => {
    for (const i in this.handlers) {
      if (this.handlers[i].callback === callback && this.handlers[i].context === context) return true;
    }
    return false;
  }

  once = (callback: (args:T) => void, context?: any) => {
    this.on(callback, context, true);
  }

  remove: EventType = (callback, context) => {
    if (this.handlers === undefined) return;
    for (let i = this.handlers.length - 1; i >= 0; i--) {
      if (this.handlers[i].callback === callback && this.handlers[i].context === context) {
        this.handlers.splice(i, 1);
      }
    }
  }

  reset: () => void = () => {
    this.handlers = undefined;
  }

  emit (args?: T) {
    if (this.handlers === undefined) return;
    const handlers = this.handlers.slice();
    for (let i = 0; i < handlers.length; i++) {
      handlers[i].callback.apply(handlers[i].context, [args]);
      if (handlers[i].once) {
        this.remove(handlers[i].callback, handlers[i].context);
      }
    }
  }
}
